import { forwardRef, ReactNode, TextareaHTMLAttributes, useEffect, useRef, useCallback } from 'react'
import { LucideIcon } from 'lucide-react'
import { CharCounter } from './CharCounter'

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> {
  value?: string
  onChange?: (value: string) => void
  leftIcon?: LucideIcon
  rightIcon?: LucideIcon
  error?: boolean
  helperText?: string
  /** Accepts nodes for a required marker (`<span className="text-destructive">*</span>`). */
  label?: ReactNode
  resize?: 'none' | 'vertical' | 'horizontal' | 'both'
  minRows?: number
  maxRows?: number
  id?: string
  /**
   * When `maxLength` is set (inherited from TextareaHTMLAttributes), a
   * `{length}/{max}` counter renders below by default. Set `false` to
   * suppress.
   */
  showCounter?: boolean
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  value = '',
  onChange,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  error = false,
  helperText,
  label,
  resize = 'vertical',
  minRows = 3,
  maxRows,
  maxLength,
  showCounter = true,
  className = '',
  disabled = false,
  placeholder,
  id,
  ...props
}, ref) => {
  const internalRef = useRef<HTMLTextAreaElement>(null)
  // Narrowed once so the counter branches get `number`, not `number | undefined`
  // behind a non-null assertion that a later edit could quietly invalidate.
  const counterMax = typeof maxLength === 'number' && showCounter ? maxLength : null

  // Helper paragraph, rendered either alone (needs its own top margin) or as
  // the left half of the footer row (the row owns the margin). Uses the
  // `muted-foreground` token so it matches `Input`'s helper text when both
  // primitives appear in the same form.
  const renderHelperText = (marginClass: string) =>
    helperText ? (
      <p
        className={`${marginClass} text-xs ${
          error ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
        }`.trim()}
      >
        {helperText}
      </p>
    ) : null

  // Combine external ref with internal ref
  const setRefs = useCallback((node: HTMLTextAreaElement | null) => {
    internalRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }, [ref])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e.target.value)
    }
  }

  // Calculate and set height based on content
  const adjustHeight = useCallback((textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Read box metrics. scrollHeight includes padding but NOT the border
    // (box-sizing: border-box), so the visible box must add the border back —
    // otherwise content overflows by the border width and a scrollbar shows at
    // every height. minRows/maxRows count visible text lines, so the chrome
    // (padding + border) is added on top.
    const style = getComputedStyle(textarea)
    const lineHeight = parseFloat(style.lineHeight) || 20
    const chrome =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
    const minHeight = lineHeight * minRows + chrome
    const maxHeight = maxRows ? lineHeight * maxRows + chrome : Infinity
    const desired = textarea.scrollHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)

    // Set height within min/max bounds, and only show a scrollbar once the
    // content actually exceeds the cap (below the cap, hide it to avoid the
    // spurious always-on scrollbar).
    const newHeight = Math.min(Math.max(desired, minHeight), maxHeight)
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = desired > maxHeight ? 'auto' : 'hidden'
  }, [minRows, maxRows])

  // Auto-resize functionality
  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    adjustHeight(e.currentTarget)
  }

  // Set initial height when component mounts or value changes
  useEffect(() => {
    if (internalRef.current) {
      adjustHeight(internalRef.current)
    }
  }, [value, adjustHeight])

  const baseClasses = [
    // Layout and sizing
    'w-full px-3 py-2 min-h-[80px]',
    // Typography
    'field-text',
    // Border and background
    'border rounded-md',
    // One fill, picked here: the classes are joined, not merged, so a second
    // fill pushed below would lose or win by stylesheet order alone.
    disabled ? 'bg-gray-50 dark:bg-transparent' : 'bg-background dark:bg-white/5',
    // Focus states
    // Ring colour is picked per branch below: joined, not merged, so a shared
    // ring colour would race the error one on stylesheet order.
    'focus:outline-none focus:ring-2 focus:border-transparent',
    // Transition
    'transition-colors duration-200',
    // Resize behavior
    resize === 'none' ? 'resize-none' : 
    resize === 'vertical' ? 'resize-y' : 
    resize === 'horizontal' ? 'resize-x' : 'resize',
  ]

  // Conditional classes based on state
  if (error) {
    baseClasses.push(
      'border-red-300 dark:border-red-600',
      'text-red-900 dark:text-red-100',
      'placeholder:text-red-400 dark:placeholder:text-red-500',
      // dark:border-red-600 would otherwise outrank the focus border.
      'dark:focus:border-transparent focus:ring-red-500'
    )
  } else {
    baseClasses.push(
      'border-field',
      'focus:ring-primary',
      // Picked here for the same reason as the fill.
      disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-foreground',
      'placeholder:text-gray-400 dark:placeholder:text-muted-foreground'
    )
  }

  if (disabled) {
    baseClasses.push(
      'cursor-not-allowed opacity-60'
    )
  }

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}
      
      <div className="relative">
        {LeftIcon && (
          <div className="absolute left-3 top-3 pointer-events-none">
            <LeftIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>
        )}
        
        <textarea
          id={id}
          name={id ? `${id}-input` : 'textarea'}
          ref={setRefs}
          value={value}
          onChange={handleChange}
          onInput={handleInput}
          disabled={disabled}
          placeholder={placeholder}
          rows={minRows}
          maxLength={maxLength}
          className={[
            ...baseClasses,
            LeftIcon ? 'pl-10' : '',
            RightIcon ? 'pr-10' : '',
            className
          ].filter(Boolean).join(' ')}
          {...props}
        />
        
        {RightIcon && (
          <div className="absolute right-3 top-3 pointer-events-none">
            <RightIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>
        )}
      </div>
      
      {/* Helper text and character counter share one row when both are
          present — the description reads left, the `{n}/{max}` counter sits
          right, so a field's footer is one line instead of two stacked
          blocks. Either one alone keeps its original standalone layout
          (hence the margin being passed in rather than baked into the
          paragraph). */}
      {helperText && counterMax !== null ? (
        <div className="mt-2 flex items-start justify-between gap-3">
          {renderHelperText('')}
          <CharCounter length={(value ?? '').length} maxLength={counterMax} inline />
        </div>
      ) : (
        <>
          {renderHelperText('mt-2')}
          {counterMax !== null && (
            <CharCounter length={(value ?? '').length} maxLength={counterMax} />
          )}
        </>
      )}
    </div>
  )
})

TextArea.displayName = 'TextArea' 